import { Download, RefreshCw, AlertCircle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAppUpdate } from "@/hooks/useAppUpdate";

export default function AppUpdateDialog() {
  const { state, manifest, progress, errorMsg, installedVersion, startDownload, dismiss } =
    useAppUpdate();

  // Only show on Android native; nothing to render on web or iOS
  if (state === "idle" || !manifest) return null;

  const forced = manifest.forceUpdate === true;

  return (
    // Full-screen overlay — pointer-events block interaction behind it
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center px-4 pb-6 sm:pb-0 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-sm rounded-2xl bg-[#121721] border border-[#1E293B] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="px-5 pt-5 pb-4 border-b border-[#1E293B]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#00E676]/10 flex items-center justify-center shrink-0">
              <RefreshCw size={20} className="text-[#00E676]" />
            </div>
            <div>
              <p className="font-semibold text-sm text-white">Update available</p>
              <p className="text-xs text-slate-400">v{manifest.versionName}</p>
            </div>
            {installedVersion && (
              <p className="ml-auto text-[10px] text-slate-500 shrink-0">
                Current: v{installedVersion}
              </p>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {manifest.releaseNotes && (
            <p className="text-xs text-slate-300 leading-relaxed">{manifest.releaseNotes}</p>
          )}

          {/* Error */}
          {errorMsg && (
            <div className="flex gap-2 rounded-xl bg-[#FF3B30]/10 border border-[#FF3B30]/30 p-3">
              <AlertCircle size={14} className="text-[#FF3B30] shrink-0 mt-0.5" />
              <p className="text-xs text-[#FF3B30]">{errorMsg}</p>
            </div>
          )}

          {/* Progress bar */}
          {state === "downloading" && (
            <div className="space-y-1.5">
              <div className="flex justify-between text-[10px] text-slate-400">
                <span>Downloading…</span>
                <span>{progress}%</span>
              </div>
              <div className="h-2 rounded-full bg-[#1E2330] overflow-hidden">
                <div
                  className="h-full rounded-full bg-[#00E676] transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Done */}
          {state === "ready" && (
            <div className="flex items-center gap-2 text-xs text-[#00E676]">
              <CheckCircle2 size={14} />
              <span>Download complete — follow the installer prompt to finish.</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-5 pb-5 flex gap-3">
          {/* Dismiss button — hidden for forced updates and while downloading */}
          {!forced && state !== "downloading" && state !== "ready" && (
            <Button
              variant="outline"
              onClick={dismiss}
              className="flex-1 h-11 border-[#1E293B] text-slate-300 bg-transparent hover:bg-[#1E2330] rounded-xl"
            >
              Later
            </Button>
          )}

          {/* Primary action */}
          {state !== "ready" && (
            <Button
              onClick={startDownload}
              disabled={state === "downloading"}
              className="flex-1 h-11 bg-[#00E676] hover:bg-[#00C853] text-black font-bold rounded-xl gap-2"
            >
              {state === "downloading" ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Downloading…
                </>
              ) : (
                <>
                  <Download size={16} />
                  {errorMsg ? "Retry" : "Download update"}
                </>
              )}
            </Button>
          )}

          {/* After install prompt is shown */}
          {state === "ready" && !forced && (
            <Button
              variant="outline"
              onClick={dismiss}
              className="flex-1 h-11 border-[#1E293B] text-slate-300 bg-transparent hover:bg-[#1E2330] rounded-xl"
            >
              Done
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
